import { message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';

// Allowed file formats
export const ALLOWED_FILE_FORMATS = {
  PDF: 'application/pdf',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  TXT: 'text/plain'
};

// Allowed image formats for the avatar
export const ALLOWED_IMAGE_FORMATS = {
  JPEG: 'image/jpeg',
  JPG: 'image/jpg',
  PNG: 'image/png',
  WEBP: 'image/webp'
};

// Allowed extensions
export const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

// Allowed image extensions
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

// String for the input accept attribute
export const FILE_ACCEPT_STRING = '.pdf,.doc,.docx,.txt';

// String for the image accept attribute
export const IMAGE_ACCEPT_STRING = '.jpg,.jpeg,.png,.webp';

/**
 * Validates that the file is in one of the allowed formats
 * @param file - File to validate
 * @returns boolean - true if valid, false if invalid
 */
export const validateFileFormat = (file: File | UploadFile): boolean => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type;

  // Check by extension
  const hasValidExtension = ALLOWED_FILE_EXTENSIONS.some(ext => 
    fileName.endsWith(ext)
  );

  // Check by MIME type (when available)
  const hasValidMimeType = Boolean(fileType && Object.values(ALLOWED_FILE_FORMATS).includes(fileType));

  // The file is valid if it has a valid extension OR a valid MIME type
  const isValid = hasValidExtension || hasValidMimeType;

  if (!isValid) {
    message.error('Only PDF, DOC, DOCX, and TXT files are allowed.');
  }

  return isValid;
};

/**
 * Validates that the image is in one of the allowed formats
 * @param file - Image file to validate
 * @returns boolean - true if valid, false if invalid
 */
export const validateImageFormat = (file: File | UploadFile): boolean => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type;

  // Check by extension
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some(ext => 
    fileName.endsWith(ext)
  );

  // Check by MIME type (when available)
  const hasValidMimeType = Boolean(fileType && Object.values(ALLOWED_IMAGE_FORMATS).includes(fileType));

  // The file is valid if it has a valid extension OR a valid MIME type
  const isValid = hasValidExtension || hasValidMimeType;

  if (!isValid) {
    message.error('Only JPG, JPEG, PNG, and WEBP images are allowed.');
  }

  return isValid;
};

/**
 * beforeUpload handler that validates the format and prevents the automatic upload
 * @param file - File to validate
 * @returns boolean - always false, to prevent the automatic upload
 */
export const validateAndPreventUpload = (file: File | UploadFile): boolean => {
  validateFileFormat(file);
  return false; // Prevent the automatic upload
}; 